import { Workflow } from '~/atoms/schemas/workflowSchemas';
import { getTasksFromWorkflow } from './useSingleWorkflow';
import { getAllTasksFromWorkflows, TaskEntry } from './useWorkflows';

/**
 * Generate task entries from workflow data
 *
 * This function handles both single workflow and multiple workflow scenarios
 *
 * @param workflows - A single workflow or an array of workflows
 * @returns Sorted array of task entries
 */
export const getTaskEntries = (workflows: Workflow[]): TaskEntry[] => {
  // For a single workflow, get tasks and sort by startedAt in descending order
  if (workflows.length === 1) {
    const tasks = getTasksFromWorkflow(workflows[0]);
    return tasks.sort(
      (a, b) =>
        new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
    );
  }

  // For all workflows, group tasks by workflow and sort within each workflow
  const allTasks = getAllTasksFromWorkflows(workflows);

  // Group tasks by workflow
  const tasksByWorkflow = allTasks.reduce(
    (acc, task) => {
      if (!acc[task.workflowId]) {
        acc[task.workflowId] = [];
      }
      acc[task.workflowId].push(task);
      return acc;
    },
    {} as Record<string, TaskEntry[]>,
  );

  // Sort tasks within each workflow by startedAt
  Object.values(tasksByWorkflow).forEach((workflowTasks) => {
    workflowTasks.sort(
      (a, b) =>
        new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
    );
  });

  // Sort workflows by their most recent task and flatten into a single array
  return Object.values(tasksByWorkflow)
    .sort((tasksA, tasksB) => {
      const latestA = tasksA[0]?.startedAt
        ? new Date(tasksA[0].startedAt).getTime()
        : 0;
      const latestB = tasksB[0]?.startedAt
        ? new Date(tasksB[0].startedAt).getTime()
        : 0;
      return latestB - latestA;
    })
    .flat();
};
